<template>
  <div class="subscription-control">
    <button
      type="button"
      class="subscription-control__trigger"
      :class="`subscription-control__trigger--${statusPresentation.tone}`"
      :disabled="isLoading"
      @click="openDialog"
    >
      <span class="subscription-control__trigger-mark">✦</span>
      <span class="subscription-control__trigger-copy">
        <span class="subscription-control__trigger-label">Подписка</span>
        <span class="subscription-control__trigger-state">{{ triggerText }}</span>
      </span>
      <span class="subscription-control__trigger-arrow">→</span>
    </button>

    <Teleport to="body">
      <Transition name="subscription-fade">
        <div
          v-if="isOpen"
          class="subscription-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="subscription-dialog-title"
          @mousedown.self="closeDialog"
        >
          <section class="subscription-dialog__panel">
            <button
              type="button"
              class="subscription-dialog__close"
              aria-label="Закрыть окно подписки"
              @click="closeDialog"
            >
              <SvgIcon icon="x" />
            </button>

            <div v-if="isLoading" class="subscription-dialog__loading">
              <span class="subscription-dialog__loader" />
              Загружаем данные подписки
            </div>

            <template v-else>
              <div v-if="dialogStep === 'overview'" class="subscription-dialog__content">
                <p class="subscription-dialog__eyebrow">
                  InFlow business
                </p>
                <div class="subscription-dialog__state" :class="`is-${statusPresentation.tone}`">
                  <span>{{ statusPresentation.icon }}</span>
                  {{ statusPresentation.badge }}
                </div>
                <h2 id="subscription-dialog-title">
                  {{ statusPresentation.title }}
                </h2>
                <p class="subscription-dialog__lead">
                  {{ statusPresentation.description }}
                </p>

                <div
                  v-if="status?.paidUntil"
                  class="subscription-dialog__date-card"
                  :class="`is-${statusPresentation.tone}`"
                >
                  <span>Оплачено до</span>
                  <strong>{{ formatDate(status.paidUntil) }}</strong>
                  <small v-if="status?.plan">{{ planName(status.plan) }}</small>
                </div>

                <div v-if="status?.mustUnpublish" class="subscription-dialog__alert">
                  <span>!</span>
                  <p>
                    Срок оплаты истёк более 7 дней назад. Приложение нужно снять с публикации
                    в сторах.
                  </p>
                </div>

                <div v-if="requestError" class="subscription-dialog__error">
                  {{ requestError }}
                </div>

                <div class="subscription-dialog__actions">
                  <button type="button" class="subscription-dialog__primary" @click="showPlans">
                    {{ statusPresentation.action }}
                    <span>→</span>
                  </button>
                  <button type="button" class="subscription-dialog__secondary" @click="refreshData">
                    Обновить статус
                  </button>
                </div>
              </div>

              <div v-else-if="dialogStep === 'plans'" class="subscription-dialog__content">
                <button type="button" class="subscription-dialog__back" @click="dialogStep = 'overview'">
                  ← Назад
                </button>
                <p class="subscription-dialog__eyebrow">
                  Оплата подписки
                </p>
                <h2 id="subscription-dialog-title">
                  Выберите удобный срок
                </h2>
                <p class="subscription-dialog__lead">
                  Без автосписаний: продление только с вашего согласия.
                </p>

                <div v-if="plans.length" class="subscription-plans">
                  <button
                    v-for="plan in plans"
                    :key="plan.code"
                    type="button"
                    class="subscription-plan"
                    :class="{ selected: selectedPlan?.code === plan.code, featured: plan.months === 12 }"
                    @click="selectPlan(plan)"
                  >
                    <span v-if="plan.months === 12" class="subscription-plan__badge">Выгодно</span>
                    <span class="subscription-plan__period">{{ planTitle(plan) }}</span>
                    <strong>{{ formatMoney(plan.amountKopecks, plan.currency) }}</strong>
                    <small>{{ planMonthlyPrice(plan) }}</small>
                    <span class="subscription-plan__select">
                      {{ selectedPlan?.code === plan.code ? 'Выбрано' : 'Выбрать' }}
                    </span>
                  </button>
                </div>
                <div v-else class="subscription-dialog__empty">
                  Тарифы пока не удалось загрузить.
                </div>

                <div v-if="requestError" class="subscription-dialog__error">
                  {{ requestError }}
                </div>
              </div>

              <div v-else-if="dialogStep === 'confirm' && selectedPlan" class="subscription-dialog__content">
                <button type="button" class="subscription-dialog__back" @click="dialogStep = 'plans'">
                  ← К тарифам
                </button>
                <p class="subscription-dialog__eyebrow">
                  Почти готово
                </p>
                <h2 id="subscription-dialog-title">
                  Подтвердите оплату
                </h2>
                <p class="subscription-dialog__lead">
                  Мы откроем защищённую страницу оплаты T‑Bank в новой вкладке.
                </p>

                <div class="subscription-dialog__summary">
                  <div>
                    <span>Тариф</span>
                    <strong>{{ planTitle(selectedPlan) }}</strong>
                  </div>
                  <div>
                    <span>К оплате</span>
                    <strong>{{ formatMoney(selectedPlan.amountKopecks, selectedPlan.currency) }}</strong>
                  </div>
                  <div>
                    <span>Автопродление</span>
                    <strong>Нет</strong>
                  </div>
                </div>

                <div v-if="requestError" class="subscription-dialog__error">
                  {{ requestError }}
                </div>
                <div class="subscription-dialog__actions">
                  <button
                    type="button"
                    class="subscription-dialog__primary"
                    :disabled="isPaymentStarting"
                    @click="startPayment"
                  >
                    {{ isPaymentStarting ? 'Создаём счёт…' : 'Перейти к оплате' }}
                    <span v-if="!isPaymentStarting">↗</span>
                  </button>
                  <p class="subscription-dialog__hint">
                    После оплаты вернитесь в эту вкладку — статус обновится.
                  </p>
                </div>
              </div>

              <div v-else-if="dialogStep === 'awaiting'" class="subscription-dialog__content subscription-dialog__content--center">
                <div class="subscription-dialog__payment-icon">
                  ↗
                </div>
                <p class="subscription-dialog__eyebrow">
                  Счёт создан
                </p>
                <h2 id="subscription-dialog-title">
                  Оплата открыта в новой вкладке
                </h2>
                <p class="subscription-dialog__lead">
                  Завершите оплату на странице T‑Bank. После успешной оплаты вернитесь сюда и обновите статус.
                </p>
                <div class="subscription-dialog__actions">
                  <button type="button" class="subscription-dialog__primary" @click="refreshAfterPayment">
                    Я оплатил — проверить
                    <span>↻</span>
                  </button>
                  <button
                    v-if="paymentUrl"
                    type="button"
                    class="subscription-dialog__secondary"
                    @click="openPaymentAgain"
                  >
                    Открыть оплату ещё раз
                  </button>
                </div>
              </div>
            </template>
          </section>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script lang="ts" src="./SubscriptionControl.ts"></script>

<style lang="scss" src="./SubscriptionControl.scss"></style>
